"use client";

import React from "react";
import { TradeItemListingLink } from "modules/tradeLists/components/RecentTrades/TradeItemListingLink";
import RecentTradesContactButton from "modules/tradeLists/components/RecentTrades/RecentTradesContactButton";
import { TradeItem } from "modules/tradeLists/model/tradeList";
import { useAuth } from "@clerk/nextjs";
import { useCardGridItemContext } from "./CardGridItemContext";
import { useMultiSelectStore } from "modules/common/stores/useMultiSelectStore";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { Handshake } from "lucide-react";

interface CardGridItemContactActionsProps {
  fromMatch?: boolean;
  /** When true, shows "Your item" text for own items instead of listing link */
  ownItemVariant?: "link" | "message";
}

export function CardGridItemContactActions({
  fromMatch = false,
  ownItemVariant = "link",
}: CardGridItemContactActionsProps) {
  const { item: baseItem, selection } = useCardGridItemContext();
  const { userId, isLoaded } = useAuth();
  // Mid-selection, navigating to a chat unmounts the list and discards the
  // selection, so the contact button can't stay a contact button.
  // Read the store rather than the cell's own selection context: cells that
  // opt out of selection (own items, inventory already fully spoken for) get
  // the no-op context, whose isAnySelected is permanently false — which would
  // leave exactly those cards acting normally while their neighbours' changed.
  // Same source of truth ContactButtonCell uses in the table views.
  const isSelecting = useMultiSelectStore((s) => s.hasSelection());
  const tradeItem = baseItem as TradeItem;
  const contactIcon = <Handshake size={ICON_SIZE} />;

  if (!tradeItem.user) return null;

  // Show skeleton while auth is loading (ownership check needs userId)
  if (!isLoaded) {
    return (
      <button
        className="btn btn-primary btn-sm pointer-events-auto! bg-primary/75! text-primary-content/75! w-full cursor-not-allowed"
        disabled
      >
        {contactIcon}
        Trade
      </button>
    );
  }

  const isOwnItem = userId === tradeItem.user.id;

  if (isOwnItem) {
    if (ownItemVariant === "message") {
      return (
        <div className="text-base-content/60 text-center text-xs">
          Your item
        </div>
      );
    }
    return <TradeItemListingLink item={tradeItem} noDivider />;
  }

  // While a selection is in progress the button toggles this card's membership
  // instead of opening a chat — navigating away would discard the selection, and
  // a dead button next to a live checkbox reads as broken. It keeps the Trade
  // look on purpose: the checkbox and the selection ring already say whether the
  // card is in, so relabelling it per-state just adds noise. Only the
  // aria-label tracks the real action. Cells that can't join the selection
  // (no-op context: fully spoken-for inventory) have nothing to toggle, so they
  // fall through and keep the button disabled.
  if (isSelecting && selection.modeType !== "none") {
    return (
      <button
        type="button"
        onClick={selection.onToggle}
        className="btn btn-primary btn-sm w-full"
        aria-label={
          selection.clearsAll
            ? "Clear selection"
            : selection.state !== "none"
              ? `Remove ${tradeItem.name} from selection`
              : `Add ${tradeItem.name} to selection`
        }
      >
        {contactIcon}
        Trade
      </button>
    );
  }

  return (
    <RecentTradesContactButton
      item={tradeItem}
      fromMatch={fromMatch}
      disabled={isSelecting}
      icon={contactIcon}
    />
  );
}
